import { NamedView, View } from "@common/types/views/views";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { pluralize } from "@common/helpers/string";
import { PlusIcon } from "@icons";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Menu } from "@design-system/Menu/Menu.lib";
import styles from "./ViewsDropdown.module.scss";

/** How a view reads in the picker, in place of its stored name. */
export interface ViewLabel {
    readonly text: string;
    /** 16px glyph or avatar before the text, in the trigger and the row. */
    readonly leading?: ReactNode;
}

interface ViewsDropdownProps<T extends View> {
    readonly builtInViews: Array<NamedView<T>>;
    readonly savedViews: Array<NamedView<T>>;
    /** Display labels by view id; views without one show their name. */
    readonly viewLabels?: ReadonlyMap<string, ViewLabel>;
    readonly selectedView: NamedView<T> | null;
    readonly onViewSelect: (view: NamedView<T> | null) => void;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly onViewDelete: (view: NamedView<T>) => void;
    readonly onViewRename: (view: NamedView<T>) => void;
    readonly onViewDuplicate: (view: NamedView<T>) => void;
    readonly onViewCreate: () => void;
}

type ViewManagementAction = "duplicate" | "rename" | "delete";

interface ViewManagementOption {
    value: ViewManagementAction;
    label: string;
    destructive?: boolean;
}

export const ViewsDropdown = <T extends View = View>({
    builtInViews,
    savedViews,
    viewLabels,
    selectedView,
    onViewSelect,
    className,
    disabled,
    onViewCreate,
    onViewDelete,
    onViewRename,
    onViewDuplicate,
}: ViewsDropdownProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const isManagementMenuOpenRef = useRef(false);

    const isBuiltInView = useCallback(
        (view: NamedView<T>) =>
            builtInViews.some((builtInView) => builtInView.id === view.id),
        [builtInViews],
    );

    const viewsById = useMemo(
        () =>
            new Map(
                [...builtInViews, ...savedViews].map((view) => [view.id, view]),
            ),
        [builtInViews, savedViews],
    );

    const matchesInput = useCallback(
        (view: NamedView<T>) => {
            const query = inputValue.trim().toLowerCase();
            const labelText = viewLabels?.get(view.id)?.text;
            return [view.name, labelText].some((text) =>
                text?.toLowerCase().includes(query),
            );
        },
        [inputValue, viewLabels],
    );

    const filteredBuiltInViews = useMemo(
        () => builtInViews.filter(matchesInput),
        [builtInViews, matchesInput],
    );

    const filteredSavedViews = useMemo(
        () => savedViews.filter(matchesInput),
        [savedViews, matchesInput],
    );

    const handleOpenChange = useCallback((open: boolean) => {
        // Keep the popup open while a per-view management menu is in use
        // (e.g. clicking "Delete" inside the nested menu must not dismiss
        // the views popup).
        if (!open && isManagementMenuOpenRef.current) {
            return;
        }
        if (!open) {
            setInputValue("");
        }
        setIsOpen(open);
    }, []);

    const handleViewSelect = useCallback(
        (viewId: string | null) => {
            onViewSelect(
                viewId === null ? null : (viewsById.get(viewId) ?? null),
            );
            setInputValue("");
            setIsOpen(false);
        },
        [onViewSelect, viewsById],
    );

    const getViewManagementOptions = useCallback(
        (view: NamedView<T>): ViewManagementOption[] => {
            const options: ViewManagementOption[] = [
                {
                    value: `duplicate`,
                    label: "Duplicate",
                },
            ];

            if (!isBuiltInView(view)) {
                options.push(
                    {
                        value: `rename`,
                        label: "Rename",
                    },
                    {
                        value: `delete`,
                        label: "Delete",
                        destructive: true,
                    },
                );
            }

            return options;
        },
        [isBuiltInView],
    );

    const handleViewManagementAction = useCallback(
        (view: NamedView<T>) => (actionValue: ViewManagementAction) => {
            switch (actionValue) {
                case "duplicate":
                    setInputValue("");
                    setIsOpen(false);
                    onViewDuplicate(view);
                    break;
                case "rename":
                    setInputValue("");
                    setIsOpen(false);
                    onViewRename(view);
                    break;
                case "delete":
                    // Don't dismiss for delete since it doesn't open a modal
                    onViewDelete(view);
                    break;
            }
        },
        [onViewDuplicate, onViewRename, onViewDelete],
    );

    const handleViewCreate = useCallback(() => {
        setInputValue("");
        setIsOpen(false);
        onViewCreate();
    }, [onViewCreate]);

    const renderViewItem = useCallback(
        (view: NamedView<T>) => (
            <Picker.Item
                key={view.id}
                value={view.id}
                className={styles.viewItem}
                leading={viewLabels?.get(view.id)?.leading}
                trailing={
                    <Picker.ItemAction>
                        <Menu.More
                            className={styles.managementTrigger}
                            onOpenChange={(open) => {
                                isManagementMenuOpenRef.current = open;
                            }}
                        >
                            {getViewManagementOptions(view).map((option) => (
                                <Menu.Item
                                    key={option.value}
                                    onClick={() =>
                                        handleViewManagementAction(view)(
                                            option.value,
                                        )
                                    }
                                    destructive={option.destructive}
                                >
                                    {option.label}
                                </Menu.Item>
                            ))}
                        </Menu.More>
                    </Picker.ItemAction>
                }
            >
                {viewLabels?.get(view.id)?.text ?? view.name}
            </Picker.Item>
        ),
        [getViewManagementOptions, handleViewManagementAction, viewLabels],
    );

    const selectedLabel = selectedView
        ? viewLabels?.get(selectedView.id)
        : undefined;

    return (
        <Picker.Select
            searchable
            value={selectedView?.id ?? null}
            open={isOpen}
            disabled={disabled}
            inputValue={inputValue}
            onValueChange={handleViewSelect}
            onInputValueChange={setInputValue}
            onOpenChange={handleOpenChange}
        >
            <Picker.ButtonTrigger
                render={
                    <Picker.Button
                        variant="primary"
                        size="md"
                        className={className}
                        leftIcon={selectedLabel?.leading}
                    />
                }
                value={selectedLabel?.text ?? selectedView?.name}
                placeholder={
                    builtInViews.length > 0
                        ? builtInViews[0].name
                        : "No views found"
                }
            />
            <Picker.Popup>
                <Picker.Search placeholder="Search views..." />
                <Picker.List>
                    {filteredSavedViews.length > 0 && (
                        <Picker.Section>
                            <Picker.SectionTitle>
                                {`Custom ${pluralize("view", filteredSavedViews.length)}`}
                            </Picker.SectionTitle>
                            {filteredSavedViews.map(renderViewItem)}
                        </Picker.Section>
                    )}
                    {filteredSavedViews.length > 0 &&
                        filteredBuiltInViews.length > 0 && <Picker.Separator />}
                    {filteredBuiltInViews.length > 0 && (
                        <Picker.Section>
                            <Picker.SectionTitle>
                                Kick views
                            </Picker.SectionTitle>
                            {filteredBuiltInViews.map(renderViewItem)}
                        </Picker.Section>
                    )}
                    {filteredBuiltInViews.length === 0 &&
                        filteredSavedViews.length === 0 && (
                            <Picker.Empty>No views found</Picker.Empty>
                        )}
                    <Picker.Footer>
                        <Picker.Item
                            leading={<PlusIcon />}
                            onSelect={handleViewCreate}
                        >
                            Create view
                        </Picker.Item>
                    </Picker.Footer>
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );
};
